import { useEffect, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { getAdminTenants, type AdminOrgInspect } from "@/lib/api";

/** TenantList surfaces every org the tracker has touched since
 *  process start — via /api/analyze push (adds to baselines) or
 *  /api/track/stream subscribe (adds to subscribers). Read-only;
 *  the admin page is runtime visibility, not org management. */
export function TenantList() {
  const [orgs, setOrgs] = useState<AdminOrgInspect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    void getAdminTenants()
      .then((r) => {
        setOrgs(r.orgs ?? []);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tenants</CardTitle>
        <p className="text-xs text-muted-foreground">
          Orgs with an active tracker footprint. Refreshes every 10s.
        </p>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-rose-500">{error}</p>}
        {loading && orgs.length === 0 && (
          <p className="text-xs text-muted-foreground">Loading…</p>
        )}
        {orgs.length === 0 && !loading && !error && (
          <p className="text-xs text-muted-foreground">
            No tenants have an active tracker footprint yet.
          </p>
        )}
        {orgs.length > 0 && (
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-4">Org ID</th>
                <th className="py-1 pr-4 text-right">Baselines</th>
                <th className="py-1 pr-4 text-right">Subscribers</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.org_id || "(legacy)"} className="border-t">
                  <td className="py-1 pr-4">{o.org_id || "(legacy / single-tenant)"}</td>
                  <td className="py-1 pr-4 text-right">{o.baselines}</td>
                  <td className="py-1 pr-4 text-right">{o.subscribers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
